import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'uf-growing-zone';
const EVENT_NAME = 'uf-zone:updated';

/**
 * First-three-digit ZIP → USPS state/territory (static; not from PRISM).
 * @type {Array<[number, number, string]>}
 */
const ZIP3_STATES = [
  [5, 5, 'NY'],
  [6, 9, 'PR'],
  [10, 27, 'MA'],
  [28, 29, 'RI'],
  [30, 38, 'NH'],
  [39, 49, 'ME'],
  [50, 54, 'VT'],
  [55, 55, 'MA'],
  [56, 59, 'VT'],
  [60, 69, 'CT'],
  [70, 89, 'NJ'],
  [90, 99, 'AE'],
  [100, 149, 'NY'],
  [150, 196, 'PA'],
  [197, 199, 'DE'],
  [200, 200, 'DC'],
  [201, 201, 'VA'],
  [202, 205, 'DC'],
  [206, 219, 'MD'],
  [220, 246, 'VA'],
  [247, 268, 'WV'],
  [270, 289, 'NC'],
  [290, 299, 'SC'],
  [300, 319, 'GA'],
  [320, 349, 'FL'],
  [350, 369, 'AL'],
  [370, 385, 'TN'],
  [386, 397, 'MS'],
  [398, 399, 'GA'],
  [400, 427, 'KY'],
  [430, 459, 'OH'],
  [460, 479, 'IN'],
  [480, 499, 'MI'],
  [500, 528, 'IA'],
  [530, 549, 'WI'],
  [550, 567, 'MN'],
  [570, 577, 'SD'],
  [580, 588, 'ND'],
  [590, 599, 'MT'],
  [600, 629, 'IL'],
  [630, 658, 'MO'],
  [660, 679, 'KS'],
  [680, 693, 'NE'],
  [700, 714, 'LA'],
  [716, 729, 'AR'],
  [730, 749, 'OK'],
  [750, 799, 'TX'],
  [800, 816, 'CO'],
  [820, 831, 'WY'],
  [832, 838, 'ID'],
  [840, 847, 'UT'],
  [850, 865, 'AZ'],
  [870, 884, 'NM'],
  [889, 898, 'NV'],
  [900, 961, 'CA'],
  [967, 968, 'HI'],
  [969, 969, 'GU'],
  [970, 979, 'OR'],
  [980, 994, 'WA'],
  [995, 999, 'AK'],
];

/**
 * @param {string} zip
 * @returns {string}
 */
const stateFromZip = (zip) => {
  const prefix = Number.parseInt(zip.slice(0, 3), 10);
  if (Number.isNaN(prefix)) return '';

  for (const [start, end, state] of ZIP3_STATES) {
    if (prefix >= start && prefix <= end) return state;
  }

  return '';
};

/**
 * @param {string} zone
 * @returns {number | null}
 */
const zoneNumber = (zone) => {
  const match = String(zone).match(/(\d{1,2})/);
  return match ? Number.parseInt(match[1], 10) : null;
};

/**
 * @param {string} raw
 * @returns {number[]}
 */
const extractZoneNumbers = (raw) => {
  if (!raw) return [];

  return (
    String(raw)
      .match(/\d{1,2}/g)
      ?.map((value) => Number.parseInt(value, 10))
      .filter((value) => value >= 1 && value <= 13) || []
  );
};

/**
 * Product Master hardiness:
 * - `custom.hardiness_zone` → "Zone 3, Zone 4, … Zone 9" (explicit set)
 * - `custom.hardiness_zones_range` → "Zones 3-9" (inclusive min–max)
 *
 * @param {string} raw
 * @returns {{ min: number, max: number, values: number[], isRange: boolean } | null}
 */
const parseRecommendedZones = (raw) => {
  const values = extractZoneNumbers(raw);
  if (!values.length) return null;

  const isRange = /[–\-]|(\d)\s*to\s*(\d)/i.test(String(raw)) && values.length >= 2;

  return {
    min: Math.min(...values),
    max: Math.max(...values),
    values,
    isRange,
  };
};

/**
 * @param {number | null} number
 * @param {{ min: number, max: number, values: number[], isRange: boolean } | null} recommended
 * @returns {boolean | null}
 */
const zoneIsRecommended = (number, recommended) => {
  if (number == null || !recommended) return null;

  if (recommended.isRange) {
    return number >= recommended.min && number <= recommended.max;
  }

  return recommended.values.includes(number);
};

/**
 * @returns {{ zip: string, zone: string, state: string } | null}
 */
export const readSavedZone = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.zip || !parsed?.zone) return null;

    return {
      zip: String(parsed.zip),
      zone: String(parsed.zone),
      state: String(parsed.state || ''),
    };
  } catch {
    return null;
  }
};

/**
 * @param {{ zip: string, zone: string, state?: string }} record
 */
const saveZone = (record) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      zip: record.zip,
      zone: record.zone,
      state: record.state || stateFromZip(record.zip),
      updatedAt: Date.now(),
    })
  );
};

/**
 * Growing-zone modal: ZIP → USDA 2023 zone, then compare to product hardiness metafields.
 *
 * @typedef {object} GrowingZoneRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} zipInput
 * @property {HTMLButtonElement} setButton
 * @property {HTMLButtonElement} [locateButton]
 * @property {HTMLButtonElement} [editButton]
 * @property {HTMLButtonElement} [doneButton]
 * @property {HTMLElement} [heading]
 * @property {HTMLElement} [intro]
 * @property {HTMLElement} [result]
 * @property {HTMLElement} [resultZone]
 * @property {HTMLElement} [resultPlace]
 * @property {HTMLElement} [resultPin]
 * @property {HTMLElement} [alert]
 * @property {HTMLElement} [alertLead]
 * @property {HTMLElement} [alertRest]
 * @property {HTMLElement} [error]
 *
 * @extends {DialogComponent}
 */
class UfGrowingZoneModal extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'zipInput', 'setButton'];

  /** @type {Record<string, string> | null} */
  #lookup = null;

  /** @type {Promise<Record<string, string> | null> | null} */
  #lookupPromise = null;

  /** @type {{ zip: string, zone: string, state: string } | null} */
  #current = null;

  /** @type {{ title: string, zones: string }} */
  #product = { title: '', zones: '' };

  connectedCallback() {
    super.connectedCallback();
    this.#current = readSavedZone();
    this.#bindOpeners();
    this.#paintBars();
    document.addEventListener(EVENT_NAME, this.#onZoneUpdated);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(EVENT_NAME, this.#onZoneUpdated);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * @param {string} [view]
   */
  openForProduct(view) {
    const nextView = view || (this.#current ? 'result' : 'set');
    this.#setView(nextView);
    this.showDialog();
    this.#preloadLookup();

    if (nextView === 'set' || nextView === 'edit') {
      requestAnimationFrame(() => this.refs.zipInput?.focus());
    }
  }

  /**
   * @param {string} view
   */
  #setView(view) {
    this.dataset.view = view;
    this.#renderResult();
    this.#clearError();

    if ((view === 'set' || view === 'edit') && this.#current) {
      this.refs.zipInput.value = this.#current.zip;
    }
  }

  #bindOpeners() {
    document.addEventListener('click', this.#onDocumentClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-uf-zone-open]') : null;
    if (!opener) return;

    event.preventDefault();

    const bar = opener.closest('[data-uf-zone-bar]');
    this.#product = {
      title: bar?.getAttribute('data-product-title') || this.dataset.productTitle || '',
      zones: bar?.getAttribute('data-product-zones') || '',
    };

    const view = opener.getAttribute('data-uf-zone-open') || '';
    this.openForProduct(view === 'edit' ? 'edit' : undefined);
  };

  #onZoneUpdated = () => {
    this.#current = readSavedZone();
    this.#paintBars();
  };

  handleEdit = () => {
    this.#setView('edit');
    requestAnimationFrame(() => this.refs.zipInput?.focus());
  };

  handleDone = () => {
    this.closeDialog();
  };

  handleLocate = async () => {
    if (!navigator.geolocation) {
      this.#showError(this.#copy('locateUnsupported'));
      return;
    }

    this.#setBusy(true);
    this.#clearError();

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 5 * 60 * 1000,
        });
      });

      const zip = await this.#zipFromCoordinates(position.coords.latitude, position.coords.longitude);
      if (!zip) {
        this.#showError(this.#copy('locateFailed'));
        return;
      }

      this.refs.zipInput.value = zip;
      await this.#applyZip(zip);
    } catch (error) {
      const denied = Boolean(error && typeof error === 'object' && 'code' in error && error.code === 1);
      this.#showError(this.#copy(denied ? 'locateDenied' : 'locateFailed'));
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const zip = this.refs.zipInput.value.replace(/\D/g, '').slice(0, 5);
    this.refs.zipInput.value = zip;

    if (!/^\d{5}$/.test(zip)) {
      this.#showError(this.#copy('zipInvalid'));
      this.refs.zipInput.focus();
      return;
    }

    this.#setBusy(true);
    this.#clearError();

    try {
      await this.#applyZip(zip);
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * @param {string} zip
   */
  async #applyZip(zip) {
    const lookup = await this.#preloadLookup();
    const zone = lookup?.[zip];

    if (!zone) {
      this.#showError(this.#copy('zipUnknown'));
      return;
    }

    const record = {
      zip,
      zone,
      state: stateFromZip(zip),
    };

    this.#current = record;
    saveZone(record);
    this.#setView('result');
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: record }));
  }

  async #preloadLookup() {
    if (this.#lookup) return this.#lookup;
    if (this.#lookupPromise) return this.#lookupPromise;

    const url = this.dataset.lookupUrl;
    if (!url) return null;

    this.#lookupPromise = this.#fetchLookup(url);
    return this.#lookupPromise;
  }

  /**
   * @param {string} url
   */
  async #fetchLookup(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('lookup failed');

      const data = await response.json();
      this.#lookup = data.z || data;
      return this.#lookup;
    } catch {
      this.#lookupPromise = null;
      this.#showError(this.#copy('lookupFailed'));
      return null;
    }
  }

  /**
   * @param {number} latitude
   * @param {number} longitude
   * @returns {Promise<string | null>}
   */
  async #zipFromCoordinates(latitude, longitude) {
    const url = new URL('https://geocoding.geo.census.gov/geocoder/geographies/coordinates');
    url.searchParams.set('x', String(longitude));
    url.searchParams.set('y', String(latitude));
    url.searchParams.set('benchmark', 'Public_AR_Current');
    url.searchParams.set('vintage', 'Current_Current');
    url.searchParams.set('format', 'json');

    const response = await fetch(url);
    if (!response.ok) return null;

    const data = await response.json();
    const geographies = data?.result?.geographies || {};
    const zcta =
      geographies['Census ZIP Code Tabulation Areas']?.[0] ||
      geographies['2020 Census ZIP Code Tabulation Areas']?.[0] ||
      geographies.ZCTAs?.[0];

    const zip = String(zcta?.ZCTA5 || zcta?.GEOID || zcta?.BASENAME || '').replace(/\D/g, '');
    return /^\d{5}$/.test(zip) ? zip : null;
  }

  #renderResult() {
    const record = this.#current;
    if (!record) return;

    const number = zoneNumber(record.zone);
    if (this.refs.resultZone) {
      this.refs.resultZone.textContent = this.#copy('zoneLabel').replace('[[zone]]', String(number ?? record.zone));
    }

    if (this.refs.resultPlace) {
      this.refs.resultPlace.textContent = [record.state, record.zip].filter(Boolean).join(' ');
    }

    const range = parseRecommendedZones(this.#product.zones);
    const inRange = zoneIsRecommended(number, range);

    this.dataset.match = inRange == null ? 'unknown' : inRange ? 'good' : 'bad';

    if (this.refs.resultPin) {
      this.refs.resultPin.classList.toggle('uf-zone-modal__pin--warning', inRange === false);
    }

    const { lead, rest } = this.#alertCopy(inRange, range, number);

    if (this.refs.alertLead) this.refs.alertLead.textContent = lead;
    if (this.refs.alertRest) this.refs.alertRest.textContent = rest ? ` ${rest}` : '';
  }

  /**
   * @param {boolean | null} inRange
   * @param {{ min: number, max: number } | null} range
   * @param {number | null} number
   * @returns {{ lead: string, rest: string }}
   */
  #alertCopy(inRange, range, number) {
    const title = this.#product.title || this.#copy('thisVariety');
    const rangeText = range ? `${range.min}–${range.max}` : '';

    if (inRange === true) {
      return {
        lead: this.#copy('successLead').replace('[[title]]', title),
        rest: this.#copy('successRest').replace('[[range]]', rangeText),
      };
    }

    if (inRange === false) {
      return {
        lead: this.#copy('warningLead'),
        rest: this.#copy('warningRest')
          .replace('[[title]]', title)
          .replace('[[range]]', rangeText)
          .replace('[[zone]]', String(number ?? '')),
      };
    }

    return {
      lead: this.#copy('zoneSaved').replace('[[zone]]', String(number ?? '')),
      rest: '',
    };
  }

  #paintBars() {
    const bars = document.querySelectorAll('[data-uf-zone-bar]');
    const record = this.#current;

    for (const bar of bars) {
      const zipEl = bar.querySelector('[data-uf-zone-zip]');
      const labelEl = bar.querySelector('[data-uf-zone-label]');
      const status = bar.querySelector('[data-uf-zone-status]');
      const statusText = bar.querySelector('[data-uf-zone-status-text]');
      const pin = bar.querySelector('[data-uf-zone-pin]');
      const action = bar.querySelector('[data-uf-zone-action]');
      const range = parseRecommendedZones(bar.getAttribute('data-product-zones') || '');
      const number = record ? zoneNumber(record.zone) : null;
      const inRange = record ? zoneIsRecommended(number, range) : null;

      bar.dataset.zoneState = record ? (inRange === false ? 'bad' : inRange === true ? 'good' : 'set') : 'pending';

      if (labelEl) {
        labelEl.textContent = record
          ? this.#copy('zoneLabel').replace('[[zone]]', String(number ?? record.zone))
          : labelEl.getAttribute('data-default-label') || this.#copy('hardinessFallback');
      }

      if (zipEl) {
        zipEl.textContent = record ? [record.state, record.zip].filter(Boolean).join(' ') : '';
      }

      if (status && statusText) {
        status.classList.remove('uf-pit__zone-status--success', 'uf-pit__zone-status--warning', 'uf-pit__zone-status--neutral');

        if (inRange === true) {
          status.classList.add('uf-pit__zone-status--success');
          statusText.textContent = this.#copy('barSuccess');
        } else if (inRange === false) {
          status.classList.add('uf-pit__zone-status--warning');
          statusText.textContent = this.#copy('barWarning');
        } else {
          status.classList.add('uf-pit__zone-status--neutral');
          statusText.textContent = record ? this.#copy('barSet') : this.#copy('barPending');
        }
      }

      if (pin) {
        pin.classList.toggle('uf-pit__zone-pin--warning', inRange === false);
      }

      if (action) {
        action.textContent = record
          ? action.getAttribute('data-label-change') || this.#copy('barChange')
          : action.getAttribute('data-label-enter') || this.#copy('enterZipFallback');
      }
    }
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.dataset.busy = busy ? 'true' : 'false';
    this.refs.setButton.disabled = busy;
    if (this.refs.locateButton) this.refs.locateButton.disabled = busy;
    this.refs.zipInput.disabled = busy;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (!this.refs.error) return;
    this.refs.error.hidden = false;
    this.refs.error.textContent = message;
  }

  #clearError() {
    if (!this.refs.error) return;
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  }

  /**
   * @param {string} key
   */
  #copy(key) {
    const datasetKey = `copy${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    return this.dataset[datasetKey] || '';
  }
}

if (!customElements.get('uf-growing-zone-modal')) {
  customElements.define('uf-growing-zone-modal', UfGrowingZoneModal);
}
